import React, { useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import type { DragSourceMonitor } from 'react-dnd';
import classNames from 'classnames';
import { getEmptyImage, NativeTypes } from 'react-dnd-html5-backend';
import styles from './CarouselThumbnail.module.scss';
import type { CarouselItem } from './Carousel';

export const CAROUSEL_ITEM_DRAG_TYPE = 'carousel-item';
const CAROUSEL_THUMBNAIL_CLASS = 'carousel-thumbnail';

export function isCarouselThumbnailElement(
  element: unknown,
): element is HTMLImageElement {
  return (
    element instanceof HTMLImageElement &&
    element.classList.contains(CAROUSEL_THUMBNAIL_CLASS)
  );
}

export type ThumbnailDragItem = {
  index: number;
  source: string;
  remove(): void;
};

export type FileDragItem = {
  index: number;
  files: File[];
  items: DataTransferItemList;
};

interface ThumbnailProps {
  image: CarouselItem;
  index: number;
  moveImage: (dragIndex: number, hoverIndex: number) => void;
  hoverFile: (index: number) => void;
  onDragEnd: (didDrop: boolean, removed: boolean) => void;
  onRemove: () => void;
  canDrag: boolean;
}

const CarouselThumbnail = ({
  image,
  index,
  moveImage,
  hoverFile,
  onDragEnd,
  onRemove,
  canDrag,
}: ThumbnailProps) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [, drop] = useDrop<ThumbnailDragItem | FileDragItem, void, unknown>({
    accept: [CAROUSEL_ITEM_DRAG_TYPE, NativeTypes.FILE],
    canDrop(item) {
      if ('files' in item && item.files.length > 0) {
        const file = item.files[0];
        return canDrag && file != null;
      }
      return canDrag;
    },
    hover(item, monitor) {
      const dragIndex = item.index;
      const hoverIndex = index;

      if (!imageRef.current) {
        return;
      }

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = imageRef.current.getBoundingClientRect();
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientX = clientOffset.x - hoverBoundingRect.left;

      if ('files' in item) {
        if (hoverClientX < hoverMiddleX) {
          hoverFile(hoverIndex);
        } else {
          hoverFile(hoverIndex + 1);
        }
        return;
      }

      if (
        (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) ||
        (dragIndex > hoverIndex && hoverClientX > hoverMiddleX)
      ) {
        return;
      }

      moveImage(dragIndex, hoverIndex);

      // This is a performance improvement suggested by the official react-dnd docs
      // See the example here https://react-dnd.github.io/react-dnd/examples/sortable/simple
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: CAROUSEL_ITEM_DRAG_TYPE,
    canDrag,
    item: (): ThumbnailDragItem => ({
      index,
      remove: onRemove,
      source: image.source,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (_item, monitor: DragSourceMonitor<unknown, { removed: boolean }>) => {
      onDragEnd(monitor.didDrop(), monitor.getDropResult()?.removed ?? false);
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(imageRef));

  return (
    <img
      ref={imageRef}
      src={image.source}
      alt={image.caption}
      className={classNames(CAROUSEL_THUMBNAIL_CLASS, styles.thumbnail, {
        [styles.isDragging]: isDragging,
      })}
      onDragStart={(e) => {
        if (!canDrag) {
          e.preventDefault();
        }
      }}
    />
  );
};

export default CarouselThumbnail;
